import { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/app/common/common-components/ui/Dialog";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/app/common/common-components/ui/RadioGroup";
import { Button } from "@/app/common/common-components/ui/Button";
import { MessageTemplate } from "@/app/models/MessageTemplateModel";
import PipelineDropdown from "@/features/deal/dealComponents/shared/PipelineDropdown";
import StageDropdown from "@/features/deal/dealComponents/shared/StageDropdown";
import { Checkbox } from "@/app/common/common-components/ui/Checkbox";
import { Badge } from "@/app/common/common-components/ui/Badge";
import SimpleTextEditor, { SimpleTextEditorRef } from "@/app/common/SimpleTextEditor";
import CursorTextarea from "@/app/common/common-components/CursorTextarea";

interface EditTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template?: MessageTemplate;
  onSave: (template: MessageTemplate) => void;
  userId: string;
  officeId: string;
  isSaving: boolean;
}

interface FormData {
  templateName: string;
  subject: string;
  templateType: "Email" | "SMS" | "Whatsapp";
  pipelineId: string;
  stageId: string;
  isSharedWithOffice: boolean;
  content: string;
}

const EditTemplateDialog: React.FC<EditTemplateDialogProps> = ({ isOpen, isSaving, onClose, template, onSave, userId, officeId }) => {
  const editorRef = useRef<SimpleTextEditorRef>(null);
  const textareaInsertRef = useRef<((text: string) => void) | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      templateName: template?.templateName || "",
      subject: template?.subject || "",
      templateType: template?.templateType || "Email",
      pipelineId: template?.pipelineId || "",
      stageId: template?.stageId || "",
      isSharedWithOffice: template?.isSharedWithOffice || false,
      content: template?.content || "",
    },
    mode: "onChange",
  });

  const watchedPipelineId = watch("pipelineId");
  const watchedType = watch("templateType");

  // Reset form when template changes
  useEffect(() => {
    if (template) {
      reset({
        templateName: template.templateName || "",
        subject: template.subject || "",
        templateType: template.templateType || "Email",
        pipelineId: template.pipelineId || "",
        stageId: template.stageId || "",
        isSharedWithOffice: template.isSharedWithOffice || false,
        content: template.content || "",
      });
    } else {
      reset({
        templateName: "",
        subject: "",
        templateType: "Email",
        pipelineId: "",
        stageId: "",
        isSharedWithOffice: false,
        content: "",
      });
    }
  }, [template, reset]);

  const onSubmit = (data: FormData) => {
    onSave({
      templateId: template?.templateId || "",
      templateName: data.templateName,
      templateType: data.templateType,
      content: data.content,
      userId,
      officeId,
      subject: data.subject,
      pipelineId: data.pipelineId,
      stageId: data.stageId,
      isSharedWithOffice: data.isSharedWithOffice,
    });
  };

  useEffect(() => {
    setValue("content", "");
  }, [watchedType]);

  const handleVariableInsert = (variableValue: string) => {
    if (watchedType === "Email") {
      // For email templates, insert at cursor position
      editorRef.current?.insertTextAtCursor(variableValue);
    } else {
      // For SMS/WhatsApp, insert at cursor position in textarea
      textareaInsertRef.current?.(variableValue);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="tw-sm:tw-max-w-md tw-max-h-[90vh] tw-overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? "Edit Template" : "New Template"}</DialogTitle>
        </DialogHeader>
        <form className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-space-y-2">
            <Label htmlFor="templateName">Name *</Label>
            <Input
              id="templateName"
              {...register("templateName", { required: "Template name is required" })}
              placeholder="Enter template name"
              disabled={isSaving}
            />
            {errors.templateName && <p className="tw-text-xs tw-text-red-500">{errors.templateName.message}</p>}
          </div>

          <div className="tw-space-y-2">
            <Label htmlFor="templateType">Type *</Label>
            <Controller
              name="templateType"
              control={control}
              rules={{ required: "Template type is required" }}
              render={({ field }) => (
                <RadioGroup value={field.value} onValueChange={field.onChange} disabled={isSaving}>
                  <div className="tw-flex tw-gap-4">
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <RadioGroupItem value="Email" id="email" />
                      <Label htmlFor="email">Email</Label>
                    </div>
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <RadioGroupItem value="SMS" id="sms" />
                      <Label htmlFor="sms">SMS</Label>
                    </div>
                    <div className="tw-flex tw-items-center tw-space-x-2">
                      <RadioGroupItem value="Whatsapp" id="whatsapp" />
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                    </div>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.templateType && <p className="tw-text-xs tw-text-red-500">{errors.templateType.message}</p>}
          </div>

          {watchedType === "Email" && (
            <div className="tw-space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                {...register("subject", {
                  validate: (value) => {
                    if (watchedType === "Email" && (!value || value.trim() === "")) {
                      return "Subject is required for email templates";
                    }
                    return true;
                  },
                })}
                placeholder="Enter template subject"
                disabled={isSaving}
              />
              {errors.subject && <p className="tw-text-xs tw-text-red-500">{errors.subject.message}</p>}
            </div>
          )}

          <div className="tw-space-y-2">
            <Label htmlFor="pipeline">Pipeline</Label>
            <Controller
              name="pipelineId"
              control={control}
              render={({ field }) => (
                <PipelineDropdown
                  type="deal"
                  officeId={officeId}
                  pipeLineId={field.value}
                  onPipeLineIdChange={field.onChange}
                  disabled={isSaving}
                  placeholder="Select pipeline (optional)"
                />
              )}
            />
          </div>

          <div className="tw-space-y-2">
            <Label htmlFor="milestone">Milestone</Label>
            <Controller
              name="stageId"
              control={control}
              render={({ field }) => (
                <StageDropdown
                  type="deal"
                  officeId={officeId}
                  pipeLineId={watchedPipelineId}
                  stageId={field.value}
                  onStageIdChange={field.onChange}
                  disabled={isSaving}
                  placeholder="Select stage (optional)"
                />
              )}
            />
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <Controller
              name="isSharedWithOffice"
              control={control}
              render={({ field }) => <Checkbox id="isSharedWithOffice" checked={field.value} onCheckedChange={field.onChange} disabled={isSaving} />}
            />
            <Label htmlFor="isSharedWithOffice">Shared with office</Label>
          </div>

          <div className="tw-space-y-2">
            <Label htmlFor="content">Content *</Label>
            <Controller
              name="content"
              control={control}
              rules={{
                required: "Content is required",
                validate: (value) => {
                  if (watchedType === "SMS" && value && value.length > 160) {
                    return "SMS content cannot exceed 160 characters";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <>
                  {watchedType === "Email" ? (
                    <SimpleTextEditor ref={editorRef} value={field.value} onChange={field.onChange} format="html" disabled={isSaving} />
                  ) : (
                    <CursorTextarea
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter template content"
                      disabled={isSaving}
                      maxLength={watchedType === "SMS" ? 160 : undefined}
                      onInsertTextAtCursor={(insertFn) => {
                        textareaInsertRef.current = insertFn;
                      }}
                    />
                  )}
                </>
              )}
            />
            {errors.content && <p className="tw-text-xs tw-text-red-500">{errors.content.message}</p>}
            {watchedType === "SMS" && <p className="tw-text-xs tw-text-muted-foreground">{watch("content")?.length || 0}/160 characters</p>}
            <p className="tw-flex tw-flex-col tw-gap-2 tw-text-xs tw-text-muted-foreground">
              <span className="tw-font-bold">Variables:</span>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {[
                  { label: "Agent First Name", value: "{{agent_first_name}}" },
                  { label: "Agent Last Name", value: "{{agent_last_name}}" },
                  { label: "Agent Email", value: "{{agent_email}}" },
                  { label: "Client First Name", value: "{{client_first_name}}" },
                  { label: "Client Last Name", value: "{{client_last_name}}" },
                  { label: "Client Email", value: "{{client_email}}" },
                ].map((variable) => (
                  <Badge key={variable.label} className="tw-cursor-pointer" onClick={() => handleVariableInsert(variable.value)} variant="outline">
                    {variable.label}
                  </Badge>
                ))}
              </div>
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTemplateDialog;
